import { useEffect, useState, type DependencyList } from 'react';

export type AsyncState<T> =
  | { status: 'loading'; data: undefined; error: undefined }
  | { status: 'success'; data: T; error: undefined }
  | { status: 'error'; data: undefined; error: Error };

/**
 * Run an async loader and expose a discriminated state. Screens render one of
 * loading / success / error from a single object — keeps every page uniform.
 * `reload()` re-runs the loader, so error states can offer a retry.
 */
export function useAsync<T>(
  loader: () => Promise<T>,
  deps: DependencyList = [],
): AsyncState<T> & { reload: () => void } {
  const [state, setState] = useState<AsyncState<T>>({
    status: 'loading',
    data: undefined,
    error: undefined,
  });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    setState({ status: 'loading', data: undefined, error: undefined });
    loader()
      .then((data) => alive && setState({ status: 'success', data, error: undefined }))
      .catch((error: Error) => alive && setState({ status: 'error', data: undefined, error }));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { ...state, reload: () => setTick((t) => t + 1) };
}
